// Sync helper: pushes a fresh snapshot of the project onto the portallink__
// doc. Most arrays (portalSchedule, portalPhotos, portalPayments, etc.)
// continue to mirror V1 shape verbatim so V1/V2 round-trip cleanly.
// portalChangeOrders[] is the one deliberate schema upgrade (V1-parity
// rebuild, schemaVersion: 2) — string ids, `name` instead of V1's
// `description`, explicit three-state portalStatus. Existing statuses are
// preserved on re-sync by keying against the prior portalChangeOrders[].
//
// Port of V1 syncPortalWithProject (public/app/index.html:20597-20686).
//
// Fire-and-forget: callers should not block on this. The function never throws
// — per-token errors are swallowed (console.warn), matching V1's behavior.

import { getShared, setShared, listMyDealLinks } from './db/shared'
import { getMarginPct } from '../hooks/useOpts'
import { createPortalLink } from './portal'
import { saveSlice } from './db'
import type { AppState } from '../types/firestore'
import type {
  Estimate, EstimateCategory, EstimateItem, Expense, Payment, Project, TmDraw, Client,
} from '../types'
import type { PortalChangeOrder, PortalPayment, PortalEstimate } from '../types/domain'

// ── resolveLinkedUid — authoritative uid lookup at write time ─────────────
//
// The heal hook (useHealClientLinkedUids) stamps linkedUid onto Client rows
// asynchronously on app mount. If a share happens before that async walk
// completes, client.linkedUid is undefined and the portallink__ doc is written
// without linkedClientUid — making it invisible on the client's side.
//
// This function closes the race: if the fast path (client.linkedUid already
// set) fails, it does a fresh listMyDealLinks() call and matches by email/name
// exactly as the heal hook does. The resolved uid is returned for the write.
//
// Exported so call sites that have a client reference can use it directly.
// Safe to call from fire-and-forget contexts — errors are caught and logged.

export async function resolveLinkedUid(
  ownerUid: string,
  client: Client | undefined,
): Promise<string | undefined> {
  if (!client) return undefined
  if (client.linkedUid) return client.linkedUid // fast path: already stamped

  // Slow path: fresh deallink lookup (one Firestore query).
  try {
    const deals = await listMyDealLinks(ownerUid)
    const clientEmail = (client.email ?? '').toLowerCase().trim()
    const clientName  = (client.name  ?? '').toLowerCase().trim()
    for (const deal of deals) {
      if (deal.status !== 'active') continue
      const raw = deal as unknown as {
        initiatorUid?: string; partnerUid?: string;
        initiatorEmail?: string; partnerEmail?: string;
        initiatorName?: string;  partnerName?: string;
      }
      if (!raw.initiatorUid || !raw.partnerUid) continue
      const otherIsInitiator = raw.partnerUid === ownerUid
      const otherUid   = otherIsInitiator ? raw.initiatorUid : raw.partnerUid
      const otherEmail = (otherIsInitiator ? raw.initiatorEmail : raw.partnerEmail)?.toLowerCase().trim()
      const otherName  = (otherIsInitiator ? raw.initiatorName  : raw.partnerName)?.toLowerCase().trim()
      if ((clientEmail && otherEmail && otherEmail === clientEmail) ||
          (clientName  && otherName  && otherName  === clientName)) {
        return otherUid
      }
    }
  } catch (e) {
    console.warn('[resolveLinkedUid] deallink lookup failed', e)
  }
  return undefined
}

// ── V1 helper ports ──────────────────────────────────────────────────────

// V1 catTotal (public/app/index.html:4917)
// V1 items ride with a single `amount` scalar; V2 items store `cost + qty`.
// Read amount first, fall back to cost*qty so estimates authored in either
// editor produce a correct category total. Matches itemLineCost in Estimates.tsx.
function itemAmount(i: EstimateItem): number {
  const legacy = (i as unknown as { amount?: number }).amount
  if (typeof legacy === 'number' && legacy > 0) return legacy
  return (i.cost ?? 0) * (i.qty ?? 0)
}

function catTotal(c: EstimateCategory): number {
  return (c.items ?? []).reduce((s, i) => s + itemAmount(i), 0)
}

// V1 paymentsForProject (public/app/index.html:5155) + totalReceivedP (:5158)
function totalReceivedP(state: AppState, project: Project): number {
  return state.payments
    .filter((p) => p.projId === project.id)
    .filter((p) => p.status === 'received')
    .reduce((s, p) => s + p.amount, 0)
}

// Per-expense field access — Expense has no `desc` field on the V2 type, but
// V1 expenses occasionally carry one. Read through unknown to stay strict.
type LegacyExpense = Expense & { desc?: string }
function coerceExpense(e: Expense): LegacyExpense {
  return e as LegacyExpense
}

// V1 expDesc (public/app/index.html:4817) — strips internal markers so client
// draw invoices don't leak plumbing like "From bid #N" or "[crew_XYZ]".
function expDesc(e: Expense): string {
  const le = coerceExpense(e)
  const rawNotes = (le.notes || '').replace(/^From bid #\d+\s*/i, '').trim()
  let t = rawNotes || le.desc || le.whoName || '—'
  t = t
    .replace(/\s*\[crew_[^\]]+\]/g, '')
    .replace(/^[\d.]+h\s*@\s*\$[\d.]+\/hr\s*—?\s*/, '')
    .trim()
  return t || '—'
}

// V1 _buildEstimateDisplayData (public/app/index.html:16040-16067).
// Rebuilds the line items / display total that the portal renders. Mirrors
// the vanilla function exactly, including pdfHideLineItems/pdfBakeMargin flags.
interface DisplayLine {
  trade: string
  desc: string
  amount: number
  hideAmt: boolean
}
interface DisplayData {
  lines: DisplayLine[]
  displayTotal: number
  showMarginLine: boolean
  margin: number
  clientPrice: number
  grandTotal: number
  hide: boolean
}

export function buildEstimateDisplayData(est: Estimate, opts: AppState['opts']): DisplayData {
  const cats = est.categories ?? []
  const grandTotal = cats.reduce((s, c) => s + catTotal(c), 0)
  const pct = getMarginPct(opts, grandTotal)
  const margin = opts.profitMarginEnabled && !est.noMargin ? grandTotal * (pct / 100) : 0
  const clientPrice = grandTotal + margin
  const hide = opts.pdfHideLineItems ?? false
  const bake = opts.pdfBakeMargin ?? false
  const displayTotal = Math.round(clientPrice)

  const lines: DisplayLine[] = []
  for (const cat of cats) {
    if (catTotal(cat) <= 0) continue
    if (hide) {
      for (const it of cat.items ?? []) {
        const desc = (it as unknown as { desc?: string }).desc ?? ''
        if (!desc) continue
        lines.push({ trade: cat.name, desc, amount: 0, hideAmt: true })
      }
    } else {
      for (const it of cat.items ?? []) {
        // Accept BOTH shapes: V1 legacy `amount` scalar, V2 `cost * qty`.
        // Earlier version read .amount only — V2-authored items (no legacy
        // amount field) fell through the filter, producing empty displayLines
        // and a $0 emailed portal total. See itemAmount() above.
        const base = itemAmount(it)
        if (base <= 0) continue
        const amount = bake && margin > 0
          ? Math.round(base * (1 + pct / 100))
          : base
        lines.push({ trade: cat.name, desc: it.desc ?? '', amount, hideAmt: false })
      }
    }
  }

  return {
    lines,
    displayTotal,
    showMarginLine: !hide && !bake && margin > 0,
    margin,
    clientPrice,
    grandTotal,
    hide,
  }
}

// PortalCO local alias removed — portalChangeOrders now uses the canonical
// PortalChangeOrder type from ../types/domain (schema-v2 shape).

// ── buildPortalCOs — pure merge helper ───────────────────────────────────
//
// Takes the GC-side source COs for a project and the prior portalChangeOrders[]
// from the existing portal doc, and returns the next portalChangeOrders[].
//
// Rules:
//   • COs in source but NOT in existing → initialised with all-'pending' statuses.
//   • COs in source AND in existing → prior portalStatus/approvalStatus/appStatus/
//     portalApprovedAt are preserved so re-sync never clobbers a client-approved CO.
//   • COs in existing but NOT in source → dropped (the CO was deleted on GC side).
//
// Exported for unit testing. Called by syncPortalWithProject.

export function buildPortalCOs(
  sourceCOs: Array<{ id: number | string; description?: string; amount?: number; date?: string }>,
  existing: PortalChangeOrder[],
): PortalChangeOrder[] {
  const existingByCOId = new Map<string, PortalChangeOrder>(
    existing.map((c) => [c.id, c]),
  )
  return sourceCOs.map((co) => {
    const id = String(co.id)
    const prior = existingByCOId.get(id)
    return {
      id,
      name: co.description ?? '',
      amount: co.amount ?? 0,
      date: co.date ?? '',
      portalStatus: prior?.portalStatus ?? 'pending',
      approvalStatus: prior?.approvalStatus ?? 'pending',
      appStatus: prior?.appStatus ?? 'pending',
      ...(prior?.portalApprovedAt !== undefined ? { portalApprovedAt: prior.portalApprovedAt } : {}),
      createdAt: prior?.createdAt ?? Date.now(),
    }
  })
}

// ── buildPortalPayments — pure merge helper ──────────────────────────────
//
// Takes the GC-side source payments for a project and the prior
// portalPayments[] from the existing portal doc, and returns the next
// portalPayments[].
//
// Payments are append-only (when a payment is received it is noted in the
// portal). The only thing to preserve across syncs is `createdAt` so the
// payment's timestamp doesn't drift on re-sync. No status flags to preserve.
//
// Field mapping: local Payment uses `notes` (plural); PortalPayment uses
// `note` (singular). Local Payment.id is a number; PortalPayment.id is string.
//
// Exported for unit testing. Called by syncPortalWithProject.

export function buildPortalPayments(
  sourcePayments: readonly Payment[],
  existing: readonly PortalPayment[],
): PortalPayment[] {
  const existingById = new Map<string, PortalPayment>(
    existing.map((p) => [p.id, p]),
  )
  return sourcePayments.map((p): PortalPayment => {
    const id = String(p.id)
    const prior = existingById.get(id)
    return {
      id,
      amount: p.amount ?? 0,
      date: p.date ?? '',
      ...(p.method !== undefined ? { method: p.method } : {}),
      ...(p.notes !== undefined ? { note: p.notes } : {}),
      createdAt: prior?.createdAt ?? Date.now(),
    }
  })
}

// ── buildPortalEstimate — pure merge helper ──────────────────────────────
//
// Builds the schema-v2 PortalEstimate snapshot that the linked client (web
// portal OR JSP client) sees when they open the portal URL.
//
// Rules:
//   • If sourceEst is undefined, returns undefined (no estimate to mirror).
//   • subtotal = Σ (item.qty * item.cost) across all categories. For legacy
//     V1-written items that carry a flat `amount` field instead of qty/cost,
//     we read through unknown so both shapes round-trip cleanly.
//   • total = subtotal * (1 + getMarginPct(opts, subtotal) / 100), respecting
//     est.noMargin. NEVER reads opts.profitMarginPct directly.
//   • portalStatus + acceptedAt are preserved from the existing portal doc so
//     a client who has already accepted is never reset to 'pending' on re-sync.
//     This is the same pattern as buildPortalCOs' status-preservation.
//
// V1 does NOT write a `portalEstimate` field — instead it writes
// displayLines, displayTotal, and showMarginLine via _buildEstimateDisplayData.
// This is a deliberate schema-v2 upgrade: a structured snapshot clients and
// app code can consume without re-deriving margins.
//
// Exported for unit testing. Called by syncPortalWithProject.

export function buildPortalEstimate(
  sourceEst: Estimate | undefined,
  existing: PortalEstimate | undefined,
  opts: AppState['opts'],
): PortalEstimate | undefined {
  if (!sourceEst) return undefined

  // Flatten all items across categories.
  // V1 items: { amount: number (flat), desc: string }
  // V2 items: { qty: number, cost: number, desc: string, id: number }
  // We read both shapes via unknown cast so legacy V1-written docs round-trip.
  type RawItem = {
    id?: number | string
    desc?: string
    qty?: number
    cost?: number
    amount?: number   // V1 legacy flat field
  }

  const allItems: Array<{ id: string; name: string; qty: number; unitPrice: number; total: number }> = []
  for (const cat of sourceEst.categories ?? []) {
    for (const it of cat.items ?? []) {
      const raw = it as unknown as RawItem
      const qty = raw.qty ?? 1
      const unitPrice = raw.cost ?? raw.amount ?? 0
      const lineTotal = raw.amount !== undefined && raw.qty === undefined
        ? raw.amount        // V1 flat amount — already the line total
        : qty * unitPrice
      if (lineTotal <= 0) continue
      allItems.push({
        id: String(raw.id ?? 0),
        name: raw.desc ?? '',
        qty,
        unitPrice,
        total: lineTotal,
      })
    }
  }

  // subtotal = sum of all line totals (pre-margin, pre-tax).
  const subtotal = allItems.reduce((s, li) => s + li.total, 0)

  // Apply tiered margin via getMarginPct — NEVER read opts.profitMarginPct directly.
  const marginPct = !sourceEst.noMargin ? getMarginPct(opts, subtotal) : 0
  const total = Math.round(subtotal * (1 + marginPct / 100))

  return {
    items: allItems,
    subtotal,
    total,
    // Preserve status + acceptedAt from existing portal doc — never reset on re-sync.
    portalStatus: existing?.portalStatus ?? 'pending',
    ...(existing?.acceptedAt !== undefined ? { acceptedAt: existing.acceptedAt } : {}),
  }
}

// ── Main sync ────────────────────────────────────────────────────────────

export async function syncPortalWithProject(
  ownerUid: string,
  projId: number,
  state: AppState,
  linkedClientUid?: string,
): Promise<void> {
  const project = state.projects.find((p) => p.id === projId)
  if (!project) return
  const est = state.estimates.find((e) => e.projId === projId)
  if (!est || !est.portalTokens || !est.portalTokens.length) return

  // V1 L20604-20607: scheduleItems — sorted by date asc, mapped to portal shape.
  // V1 reads `task` and `subName` off the schedule row. V2's Schedule type
  // exposes `title` (the vanilla writer aliased it); fall back to the raw
  // field names via unknown cast so V1-written rows still round-trip.
  const scheduleItems = state.schedules
    .filter((s) => s.projId === projId)
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .map((s) => {
      const raw = s as unknown as { task?: string; subName?: string }
      return {
        task: raw.task ?? s.title ?? '',
        subName: raw.subName ?? '',
        date: s.date || '',
        notes: s.notes || '',
      }
    })

  // V1 L20608-20611: recentPayments — latest 5, newest first.
  const recentPayments = state.payments
    .filter((p) => p.projId === projId)
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .slice(0, 5)
    .map((p) => ({
      amount: p.amount,
      date: p.date,
      status: p.status || '',
      notes: p.notes || '',
    }))

  // V1 L20612: payReceived
  const payReceived = totalReceivedP(state, project)

  // Build source CO and payment lists from project state.
  const sourceCOs = state.changeOrders.filter((c) => c.projId === projId)
  const sourcePayments = state.payments.filter((p) => p.projId === projId)

  // V1 L20625: iterate each token — one shared doc per token.
  for (const token of est.portalTokens) {
    try {
      const docId = 'portallink__' + token
      const existing = await getShared<{
        ownerUid?: string
        linkedClientUid?: string
        participantUids?: string[]
        schemaVersion?: number
        portalChangeOrders?: PortalChangeOrder[]
        portalPayments?: PortalPayment[]
        portalEstimate?: PortalEstimate
      }>(docId)
      // V1 L20628: skip if missing or owned by a different user.
      if (!existing || existing.ownerUid !== ownerUid) continue

      // Schema-v2: build portalChangeOrders[], merging in any existing client-
      // side status so re-sync never clobbers a client-approved CO.
      const portalChangeOrders = buildPortalCOs(sourceCOs, existing.portalChangeOrders ?? [])

      // V1 L20634: coAdjustedTotal — sum of CO amounts that are approved
      // internally OR approved by the client through the portal.
      const coAdjustedTotal = portalChangeOrders
        .filter((c) => c.appStatus === 'approved' || c.portalStatus === 'client-approved')
        .reduce((s, c) => s + (c.amount || 0), 0)

      // V1 L20635-20660: T&M-only extras.
      //
      // IMPORTANT: setShared uses { merge: true }, so omitting fields does
      // NOT clear them on the portal doc. V1 has the same bug — deleting
      // the last draw left stale `tmDraws` / totals on the client portal.
      // Fix: when the project is T&M, always write the fields — even when
      // tmDraws is empty — so the portal reflects the deletion.
      const tmExtras: Record<string, unknown> = {}
      if (project.billingType === 'time_material') {
        const draws: TmDraw[] = project.tmDraws ?? []
        const tmTotalBilled = draws.reduce((s, d) => s + d.total, 0)
        const tmTotalPaid = draws
          .filter((d) => d.status === 'paid')
          .reduce((s, d) => s + d.total, 0)
        tmExtras.billingType = 'time_material'
        tmExtras.tmDraws = draws.map((d) => {
          const items = (d.items ?? [])
            .map((id) => state.expenses.find((e) => e.id === id))
            .filter((e): e is Expense => Boolean(e))
          const feeMul = (d.feePct || 0) / 100
          return {
            id: d.id,
            number: d.number,
            date: d.date,
            dateRange: d.dateRange || {},
            total: d.total,
            status: d.status,
            paidAt: d.paidAt,
            invoiceRef: d.invoiceRef,
            itemCount: items.length,
            lineItems: items.map((e) => ({
              cat: e.cat || '—',
              desc: expDesc(coerceExpense(e)),
              amount: Math.round(e.amount * (1 + feeMul)),
            })),
          }
        })
        tmExtras.tmTotalBilled = tmTotalBilled
        tmExtras.tmTotalPaid = tmTotalPaid
        tmExtras.tmBalanceDue = tmTotalBilled - tmTotalPaid
      }

      // V1 L20661: display data for the client-facing estimate view.
      const display = buildEstimateDisplayData(est, state.opts)

      // Schema-v2: structured estimate snapshot for the portal. Preserves
      // portalStatus/acceptedAt from the prior doc so client acceptance is
      // never wiped on re-sync. (V1 had no equivalent field — this is a
      // deliberate schema-v2 upgrade.)
      const portalEstimate = buildPortalEstimate(est, existing.portalEstimate, state.opts)

      // V1 L20662-20682: write the portal doc. setShared already stamps
      // _updatedAt: serverTimestamp, so we just add lastSyncedAt explicitly.

      // Rebuild participantUids from ownerUid + linkedClientUid.
      // Resolution order: (1) already on the doc (set by prior heal/create),
      // (2) passed in by the caller (resolved from Client.linkedUid at share
      // time). When (2) supplies the uid and (1) doesn't have it yet, we also
      // write linkedClientUid onto the doc so future syncs self-heal.
      const storedLinkedUid = existing.linkedClientUid
      const resolvedLinkedUid = storedLinkedUid ?? linkedClientUid
      const participantUids = [
        ownerUid,
        ...(resolvedLinkedUid !== undefined ? [resolvedLinkedUid] : []),
      ]

      const payload: Record<string, unknown> = {
        projId,
        projName: project.name,
        projStatus: project.status || 'In progress',
        scheduleItems,
        payBudget: project.budget || 0,
        payReceived,
        payOutstanding: Math.max(0, (project.budget || 0) - payReceived),
        recentPayments,
        portalChangeOrders,
        coAdjustedTotal,
        displayLines: display.lines,
        displayTotal: display.displayTotal,
        showMarginLine: display.showMarginLine,
        ...tmExtras,
        lastSyncedAt: new Date().toISOString(),
        // Schema-v2 fields required by Firestore rules (Task 3) and linked-
        // client read query (Task 9). Safe to re-stamp on every sync.
        participantUids,
        schemaVersion: 2,
        // Stamp linkedClientUid if we're filling it in for the first time.
        // Conditional spread keeps the field absent when not known, satisfying
        // exactOptionalPropertyTypes and Firestore's no-undefined rule.
        ...(resolvedLinkedUid !== undefined && storedLinkedUid === undefined
          ? { linkedClientUid: resolvedLinkedUid }
          : {}),
        // Mirror all project payments to the portal on every sync.
        // buildPortalPayments preserves createdAt from prior entries so
        // timestamps don't drift across re-syncs (Task 6).
        portalPayments: buildPortalPayments(sourcePayments, existing.portalPayments ?? []),
        // Schema-v2 structured estimate snapshot. Conditional spread keeps the
        // field absent (not undefined) when no estimate is available, satisfying
        // exactOptionalPropertyTypes and Firestore's no-undefined rule.
        ...(portalEstimate !== undefined ? { portalEstimate } : {}),
      }

      await setShared(docId, payload)
    } catch (err) {
      // V1 L20683-20685: per-token errors logged, loop continues.
      console.warn('[Portal] sync failed for token', token, err)
    }
  }
}

// ── Auto-create-then-sync (V1 parity) ─────────────────────────────────────
// In V1, every project that passes through _showPortalLinkModal / the auto
// portal flow gets a portallink__<token> doc so the receiver-side "Web portal
// link" section always has something to resolve. In V2 we used to only create
// the portal doc lazily when the GC opened the Client Portal modal — so many
// projects had no token and the receiver saw "Portal link not available".
//
// This helper closes that gap. It:
//   1. Finds (or no-ops if missing) the estimate linked to this project.
//   2. If no linked estimate exists, auto-creates a bare stub estimate (V1
//      sendProjectToClientPortal does the same at public/app/index.html:11291).
//   3. If the estimate has no portalTokens, mints one via createPortalLink
//      and writes it back onto the estimate via saveSlice.
//   4. Finally calls syncPortalWithProject to stamp the full portal payload.
//
// Fire-and-forget: callers should `void` it. Never throws — errors are
// swallowed with console.warn.
export async function ensurePortalLinkForProject(
  ownerUid: string,
  projId: number,
  stateIn: AppState,
  linkedClientUid?: string,
): Promise<void> {
  // Use a local mutable reference so we can patch clients/estimates in-place
  // without mutating the caller's object.
  let state = stateIn
  const project = state.projects.find((p) => p.id === projId)
  if (!project) return
  // Received projects belong to the sender — we never mint a portal for them.
  const rawProj = project as unknown as { _received?: boolean }
  if (rawProj._received) return

  try {
    let est = state.estimates.find((e) => e.projId === projId)

    // Create a bare stub estimate if none exists (V1 parity: V1 auto-creates
    // one inside sendProjectToClientPortal when project has no estimate).
    // We do NOT mutate the caller's state here — callers that need the stub
    // written back should do it in their own mutation (add / convertToProject
    // already write the estimate explicitly; heal does it below).
    if (!est) {
      // Let callers that want the stub persisted handle it. Without an
      // estimate we can't satisfy the portallink__ create rule (estId is
      // required), so bail — the heal hook will retry next session once
      // the caller creates the estimate.
      return
    }

    // Resolve the linked client uid authoritatively at write time.
    // Fast path: client.linkedUid is already stamped (by the heal hook or a
    // prior run). Slow path: the heal hook hasn't finished yet (timing race) —
    // resolveLinkedUid does a fresh listMyDealLinks() call to find the uid.
    // This collapses the race so the portallink__ doc is always written with
    // linkedClientUid when a deallink exists, regardless of heal-hook timing.
    const client = project.clientId != null
      ? state.clients.find((c) => c.id === project.clientId)
      : undefined
    const resolvedLinkedClientUid = linkedClientUid
      ?? (await resolveLinkedUid(ownerUid, client))

    // If resolveLinkedUid found a uid that wasn't yet on the Client row,
    // persist it back so future calls take the fast path (no extra Firestore
    // query) and the heal hook has nothing to do on next session.
    if (resolvedLinkedClientUid !== undefined && client && !client.linkedUid) {
      const patchedClients = state.clients.map((c): Client =>
        c.id === client.id ? { ...c, linkedUid: resolvedLinkedClientUid } : c,
      )
      try {
        await saveSlice(ownerUid, 'clients', patchedClients)
        console.warn(
          '[Portal] stamped linkedUid on client', client.id, client.name,
          '->', resolvedLinkedClientUid,
        )
        // Patch local state reference so the sync step below sees the update.
        state = { ...state, clients: patchedClients }
      } catch (err) {
        console.warn('[Portal] persist linkedUid on client failed', err)
      }
    }

    const hasToken = Array.isArray(est.portalTokens) && est.portalTokens.length > 0
    if (!hasToken) {
      const { token } = await createPortalLink({
        ownerUid, estimate: est, client, project, opts: state.opts,
        ...(resolvedLinkedClientUid !== undefined ? { linkedClientUid: resolvedLinkedClientUid } : {}),
      })
      // Persist the token back onto the estimate row. We write via saveSlice
      // so we don't race with any concurrent saveAppState — saveSlice is a
      // per-slice write that the realtime listener merges.
      const nextEstimates = state.estimates.map((e): Estimate => {
        if (e.id !== est!.id) return e
        const existing = e.portalTokens ?? []
        if (existing.includes(token)) return e
        return { ...e, portalTokens: [...existing, token] }
      })
      try { await saveSlice(ownerUid, 'estimates', nextEstimates) } catch (err) {
        console.warn('[Portal] persist portalToken failed', err)
      }
      // Use the patched estimate slice for the sync step below.
      est = nextEstimates.find((e) => e.id === est!.id)!
    }

    // Now run the full sync against the state. Pass a shallow clone with the
    // refreshed estimates slice so syncPortalWithProject sees the token we
    // just minted (the caller's `state` reference may still be stale).
    const patchedState: AppState = {
      ...state,
      estimates: state.estimates.map((e) => e.id === est!.id ? est! : e),
    }
    await syncPortalWithProject(ownerUid, projId, patchedState, resolvedLinkedClientUid)
  } catch (err) {
    console.warn('[Portal] ensurePortalLinkForProject failed', err)
  }
}
